/**
 * Config-driven App-mode menus: Menu nodes from the designer tree via
 * GET /api/v1/designer/menus. The configuration is the source of truth for
 * navigation; the frontend module registry stays a fallback for modules with
 * no Menu node.
 */
import { useQuery } from 'react-query';

import { TokenManager } from '@/utils/api';
import { useAppLocale } from '@/app/permissions/useAppLocale';
import type { ConfigMenu } from './configMenuNav';

export type { ConfigMenu, ConfigMenuItem, ConfigNavItem } from './configMenuNav';
export { configMenusToNavItems } from './configMenuNav';

export function useConfigMenus(connectionId: string | undefined): {
  menus: ConfigMenu[];
  isLoading: boolean;
} {
  const { locale } = useAppLocale();
  const { data, isLoading } = useQuery<ConfigMenu[]>(
    ['config-menus', connectionId, locale ?? ''],
    async () => {
      const token = TokenManager.getAccessToken();
      if (!token || !connectionId) return [];
      const resp = await fetch(
        `/api/v1/designer/menus?connection_id=${encodeURIComponent(connectionId)}&locale=${encodeURIComponent(locale ?? '')}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) return [];
      const payload = await resp.json();
      return Array.isArray(payload?.data) ? (payload.data as ConfigMenu[]) : [];
    },
    { enabled: !!connectionId, staleTime: 30_000 },
  );
  return { menus: data || [], isLoading };
}
